import { createStyles, lighten, makeStyles, Theme, Tooltip } from '@material-ui/core';
import { Edit, ReportProblemOutlined } from '@material-ui/icons';
import { RenderRichText } from '@talentnow/shared-ui/components';
import moment from 'moment';
import React, { useMemo } from 'react';
import TnTypography from '../../../../_components/tn-typography/TnTypography';

import EducationForm, { EducationEntity } from './EducationForm';

interface EducationHandlers {
  onEditEducation: (education: EducationEntity) => void;
  onDeleteEducation: () => void;
}

interface Props {
  education: EducationEntity;
  changeHandlers: EducationHandlers;
  onFormOpenChange?: (isOpen: boolean) => void;
  onFormValidityChange?: (isValid: boolean, invalidFields: string[]) => void;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    education: {
      width: '100%',
      justifyContent: 'space-between',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      '&:not(:last-child)': {
        borderBottom: `1px solid #E0E0E0`,
        paddingBottom: 8,
      },
    },
    headerContainer: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      width: '100%',
      alignItems: 'center',
    },
    headerTextSection: {
      display: 'flex',
      flexDirection: 'column',
    },
    headerButtonSection: {
      display: 'flex',
      flexDirection: 'row',
      gap: 8,
      alignItems: 'center',
    },
    editIconContainer: {
      cursor: 'pointer',
      border: `1px solid ${lighten(theme.palette.primary.main, 0.5)}`,
      padding: '4px 4px 0px 4px',
      borderRadius: 4,
    },
    editIcon: {
      fill: theme.palette.primary.main,
    },
  }),
);

export default function Education({
  education,
  changeHandlers,
  onFormOpenChange,
  onFormValidityChange,
}: Readonly<Props>) {
  const classes = useStyles();

  const [editFormOpen, setEditFormOpen] = React.useState(false);

  const formIsInvalid = useMemo(() => {
    return (
      !education.degreeName ||
      !education.schoolName ||
      (education.startDate && education.endDate && moment(education.endDate).isBefore(moment(education.startDate)))
    );
  }, [education]);

  return (
    <div className={classes.education}>
      <div className={classes.headerContainer}>
        <div className={classes.headerTextSection}>
          <TnTypography bold variant="caption">
            {education.schoolName} &#x2022; {education.degreeName}
          </TnTypography>
          {(education.startDate || education.endDate) && (
            <TnTypography color="lightGrey" variant="caption2">
              {education.startDate && moment(education.startDate).format('MMMM YYYY')}
              {education.startDate && ' - '}
              {education.endDate ? moment(education.endDate).format('MMM YYYY') : 'Present'}
            </TnTypography>
          )}
        </div>
        <div className={classes.headerButtonSection}>
          {formIsInvalid && (
            <Tooltip title="Missing required fields or invalid date range" placement="top">
              <div style={{ color: '#F57C00' }}>
                <ReportProblemOutlined />
              </div>
            </Tooltip>
          )}
          <div className={classes.editIconContainer}>
            <Edit
              className={classes.editIcon}
              onClick={() => {
                const nextState = !editFormOpen;
                setEditFormOpen(nextState);
                onFormOpenChange?.(nextState);
              }}
            />
          </div>
        </div>
      </div>
      {education.additionalInfo && (
        <div style={{ marginTop: -8 }}>
          <RenderRichText maxLines={2} smallFont richTextValue={education.additionalInfo} />
        </div>
      )}

      {editFormOpen && (
        <div style={{ marginTop: 8 }}>
          <EducationForm
            mode="edit"
            onDelete={() => {
              changeHandlers.onDeleteEducation();
              onFormOpenChange?.(false);
            }}
            onSubmit={(updatedEducation) => {
              changeHandlers.onEditEducation(updatedEducation);
              setEditFormOpen(false);
              onFormOpenChange?.(false);
            }}
            onCancel={() => {
              setEditFormOpen(false);
              onFormOpenChange?.(false);
            }}
            education={education}
            onValidityChange={onFormValidityChange}
          />
        </div>
      )}
    </div>
  );
}
